<template>
	<div class="box d-print-noborder" :class="borderCheck">
		<div class="level" :class="{ 'is-marginless': moveChartIcons }">
			<div class="level-left">
				<h3
					v-if="heading"
					class="title is-4 has-text-weight-bold"
					v-text="heading"
				/>
			</div>
			<div class="level-right apex-download-options">
				<button
					v-if="active"
					class="button is-small gtm-download-image level-item"
					@click.prevent="saveChartImage"
				>
					<dc-icon icon="image-polaroid" />
					<span>Download image</span>
				</button>
				<a
					v-if="showDownload"
					class="button is-small gtm-download-excel level-item"
					:href="downloadURL"
					target="_blank"
				>
					<dc-icon icon="download" />
					<span>{{ downloadLabel }}</span>
				</a>
			</div>
		</div>
		<vue-apex-charts
			v-if="active"
			ref="apex-chart"
			:options="options"
			:series="active && series"
			:height="height"
			:width="width"
			:class="[
				'apex-chart',
				{ 'move-burger': moveChartIcons },
				{ 'download-file': showDownload }
			]"
		></vue-apex-charts>
		<div v-else>
			<dc-icon v-if="isFetching" icon="sync" spin />
			<span v-else>There is no data to display</span>
			&nbsp;<span v-text="label" />
		</div>
	</div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { mapActions } from "vuex";
import format from "date-fns/format";
import FileSaver from "file-saver";

export default {
	name: "ApexCharts",
	components: { VueApexCharts },
	props: {
		data: {
			type: Object,
			default: () => null
		},
		source: {
			type: String,
			default: () => null
		},
		downloadsource: {
			type: String,
			default: () => null
		}
	},
	data() {
		return {
			options: {},
			series: null,
			active: false,
			isFetching: false,
			label: "Loading",
			height: 150,
			width: "100%"
		};
	},
	computed: {
		heading() {
			return this.data.title;
		},
		borderCheck() {
			return this.data.hideborder ? "no-border px-0" : "";
		},
		showDownload() {
			return this.active && this.downloadsource !== null;
		},
		downloadURL() {
			return this.downloadsource;
		},
		downloadLabel() {
			return this.data.downloadlabel !== undefined
				? this.data.downloadlabel
				: "Download";
		},
		moveChartIcons() {
			return this.data.spacing && this.data.spacing >= 8;
		}
	},
	watch: {
		source(path) {
			this.fetchData(path);
		}
	},
	mounted() {
		if (this.source) {
			this.fetchData(this.source);
		}
	},
	methods: {
		...mapActions({
			fetchSource: "reportRequests/fetch",
			isValid: "reportRequests/valid"
		}),
		async fetchData() {
			this.active = false;
			this.isFetching = true;
			this.chartData = null;
			this.label = "Loading";

			if (!this.isValid(this.source)) {
				this.isFetching = false;
				return;
			}

			await this.fetchSource(this.source).then(
				(response) => {
					this.updateChart(response);
					this.isFetching = false;
					if (!response) {
						this.label = this.errorMessage;
					}
				},
				() => {
					this.label = this.errorMessage;
					this.isFetching = false;
				}
			);
		},
		updateChart(graph) {
			if (!graph) {
				return false;
			}
			const { options, series } = graph;

			options.chart.toolbar = {
				tools: {
					download: false
				}
			};
			this.series = series;

			const hasData = !!series.length;

			if (!hasData) {
				this.label = "";
				return false;
			}

			this.options = options;
			this.active = true;

			const { chart } = options;
			if (chart && chart.height > 0) {
				this.height = chart.height;
			}
		},
		async saveChartImage(chartId) {
			const chartInstance = this.$refs["apex-chart"];
			const base64 = await chartInstance.chart.dataURI();

			if (base64.imgURI) {
				const date = format(new Date(), "ddMMyyyy_HHmmss");
				const name = this.heading.replace(/\s+/g, "-").toLowerCase();
				const filename = `${name}-chart-${date}.png`;

				const pngFile = this.dataURItoBlob(base64.imgURI);
				FileSaver.saveAs(pngFile, filename);
			}
		},
		dataURItoBlob(dataURI) {
			// convert base64 to raw binary data held in a string
			// doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
			var byteString = atob(dataURI.split(",")[1]);

			// separate out the mime component
			var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

			// write the bytes of the string to an ArrayBuffer
			var ab = new ArrayBuffer(byteString.length);

			// create a view into the buffer
			var ia = new Uint8Array(ab);

			// set the bytes of the buffer to the correct values
			for (var i = 0; i < byteString.length; i++) {
				ia[i] = byteString.charCodeAt(i);
			}

			// write the ArrayBuffer to a blob, and you're done
			var blob = new Blob([ab], { type: mimeString });
			return blob;
		}
	}
};
</script>
<style lang="scss">
.apex-download-options {
	display: none;
	@include widescreen {
		display: flex;
	}
}
</style>
<style scoped>
div.level {
	margin-bottom: 0.75rem;
}
.column-title {
	min-height: auto;
	/* margin-top: 2.5em; */
}
.no-border {
	border: none !important;
	box-shadow: none;
}

@media print {
	.box {
		border: none !important;
		box-shadow: none;
		padding-left: 0;
		padding-right: 0;
	}
}
.apex-chart.move-burger >>> .apexcharts-canvas {
	position: static;
}
.apex-chart.move-burger >>> .apexcharts-toolbar {
	top: 1.55rem !important;
	right: 11.5rem !important;
	z-index: 4;
}

.apex-chart.move-burger.download-file >>> .apexcharts-toolbar {
	right: 22rem !important;
}

.apex-chart >>> .apexcharts-element-hidden {
	display: none;
}

.apex-chart >>> .apexcharts-menu-item.exportCSV {
	display: none;
}
</style>
